import {
  createContext, ReactElement, ReactNode, useCallback, useContext, useMemo,
  useState,
} from 'react';
import { FilterOptions, ViewOption } from '../types';

type SelectedOptions = {
  state: string[] | undefined,
  status: string | undefined,
  group: { value: string, units: number[] } | undefined,
  healthStatus: string | undefined,
}

export interface FilterContext {
  client: string | undefined;
  filterOptions: FilterOptions;
  viewOption: ViewOption;
  selectedOptions: SelectedOptions;
  filtersDependencies: unknown[];
  clearFilter: (key: keyof SelectedOptions) => void;
  onSelectOption: (key: keyof SelectedOptions, value: string, additionalValue?: number[]) => void;
  onSelectView: (view: ViewOption) => void;
  onSelectClient: (value: string) => void;
  setFilterOptions: (filterOptions: FilterOptions) => void;
}

const FilterContext = createContext<FilterContext | undefined>(undefined);

export const FilterProvider = ({
  children,
}: Readonly<{ children: ReactNode }>): ReactElement => {
  const [client, setClient] = useState<string>();
  const [viewOption, setViewOption] = useState<ViewOption>('health');
  const [selectedOptions, setSelectedOptions] = useState<SelectedOptions>({
    group: undefined,
    state: undefined,
    status: undefined,
    healthStatus: undefined,
  });
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({
    states: [],
    groups: [],
    clients: [],
  });

  const hasOptionSelected = useCallback((key: keyof SelectedOptions, value: string): boolean => {
    if (key === 'state') return !!selectedOptions.state?.includes(value);
    if (key === 'group') return selectedOptions[key]?.value === value;

    return selectedOptions[key] === value;
  }, [selectedOptions]);

  const filtersDependencies = useMemo(() => [
    client,
    selectedOptions.group?.units,
    selectedOptions.state,
    selectedOptions.healthStatus,
    selectedOptions.status,
  ], [
    client,
    selectedOptions.group?.units,
    selectedOptions.healthStatus,
    selectedOptions.state,
    selectedOptions.status,
  ]);

  const onSelectOption = useCallback((key: keyof SelectedOptions, value: string, additionalValue?: number[]):void => {
    setSelectedOptions((prevValues) => {
      const newValues = { ...prevValues };
      if (key === 'group') {
        if (hasOptionSelected(key, value)) {
          newValues[key] = undefined;
        } else {
          newValues[key] = {
            value,
            units: additionalValue ?? [],
          };
        }

        return newValues;
      }

      if (key === 'state') {
        const stateOptions = prevValues.state ?? [];
        if (stateOptions.includes(value)) {
          newValues[key] = stateOptions.filter((item) => item !== value);
        } else {
          newValues[key] = [...stateOptions, value];
        }

        return newValues;
      }

      if (hasOptionSelected(key, value)) {
        newValues[key] = undefined;
      } else {
        newValues[key] = value;
      }
      return newValues;
    });
  }, [hasOptionSelected]);

  function onSelectView(view: ViewOption): void {
    setViewOption(view);
    if (view === 'health') {
      clearFilter('status');
    } else {
      clearFilter('healthStatus');
    }
  }

  function onSelectClient(value: string): void {
    const clientChanged = client !== value;
    if (clientChanged) {
      setSelectedOptions((prevOptions) => ({
        ...prevOptions,
        status: undefined,
        group: undefined,
        state: undefined,
      }));
    }
    setClient(value);
    clearFilter('group');
    clearFilter('healthStatus');
    clearFilter('status');
    clearFilter('state');
  }

  function clearFilter(key: keyof SelectedOptions): void {
    setSelectedOptions((prevOptions) => ({
      ...prevOptions,
      [key]: undefined,
    }));
  }

  const value = useMemo((): FilterContext => ({
    client,
    clearFilter,
    onSelectClient,
    onSelectOption,
    onSelectView,
    selectedOptions,
    viewOption,
    filtersDependencies,
    filterOptions,
    setFilterOptions,
  }), [
    client,
    onSelectOption,
    selectedOptions,
    viewOption,
    filtersDependencies,
    filterOptions,
  ]);

  return (
    <FilterContext.Provider value={value}>{children}</FilterContext.Provider>
  );
};

export const useFilterContext = (): FilterContext => {
  const context = useContext(FilterContext);

  if (!context) {
    throw new Error('useFilter must be used within a FilterProvider.');
  }

  return context;
};
